import { useMemo } from 'react';
import { FormatterType } from '@fabric-ui/components';
import useQueryObject from '@hooks/useQueryObjectSql';
import { keepPreviousData } from '@tanstack/react-query';
import { and, field, or } from '@utils/filter';
import { DwQueryRequest } from '@views/CustomDashboards/types';
import { MappingTreeNode } from '@views/Sources/Mapping/types';

const typeToFormatterResolver = {
  INTEGER: FormatterType.number,
  Number: FormatterType.number,
  BOOLEAN: FormatterType.boolean,
  Array: FormatterType.array,
  Object: FormatterType.json,
  VARCHAR: FormatterType.string,
  Text: FormatterType.string,
};

const buildTreeFromKeyList = (keys: string[], fieldTypes: Array<{ path: string; type: string }> = []): MappingTreeNode[] => {
  const root = {};

  keys.forEach(key => {
    const parts = key.split('.');
    parts.reduce((currentNode, part) => {
      const nextNode = currentNode[part] || {};
      // eslint-disable-next-line no-param-reassign
      currentNode[part] = nextNode;
      return nextNode;
    }, root);
  });

  const convertToNodeArray = (node: object, prefix = ''): MappingTreeNode[] =>
    Object.keys(node).map(key => {
      const currentPath = prefix ? `${prefix}.${key}` : key;
      const children = convertToNodeArray(node[key], currentPath);
      const fieldType = fieldTypes.find(({ path }) => path === currentPath)?.type;
      const type = fieldType ? typeToFormatterResolver[fieldType] : null;

      return {
        label: key,
        path: currentPath,
        children,
        isMissing: false,
        ...(type ? { type } : {}),
      };
    });

  return convertToNodeArray(root);
};

export const buildKeyTree = (
  data: Record<string, any> | string[],
  fieldTypes: Array<{ path: string; type: string }> = [],
  prefix = ''
): MappingTreeNode[] => {
  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
    return buildTreeFromKeyList(data as string[], fieldTypes);
  }

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return [];
  }

  return Object.keys(data).map(key => {
    const value = data[key];
    const currentPath = prefix ? `${prefix}.${key}` : key;
    const children = Array.isArray(value) ? [] : buildKeyTree(value, fieldTypes, currentPath);
    const fieldType = fieldTypes.find(({ path }) => path === currentPath)?.type;
    const type = fieldType ? typeToFormatterResolver[fieldType] : null;

    return {
      label: key,
      path: currentPath,
      children,
      isMissing: false,
      ...(type ? { type } : {}),
    };
  });
};

export const useMappingFileFields = ({
  instanceId,
  workflowId,
  enabled,
}: {
  instanceId?: string;
  workflowId?: string;
  enabled?: boolean;
}) => {
  const sourceFilter = instanceId
    ? field('rawlogs.dsi_id').str.equals(instanceId)
    : or(field('rawlogs.dsi_id').str.equals(''), field('rawlogs.dsi_id').str.empty());

  const workflowFilter = field("AVALOR_CONTEXT(rawlogs, 'workflowId')").str.equals(workflowId as string);

  const fieldTypesQuery: DwQueryRequest = {
    select: {
      dims: [
        {
          name: 'COLLECT_DISTINCT(DESCRIBE_JSON(rawlogs.document, true))',
          alias: 'fieldTypes',
        },
      ],
      metrics: [],
    },
    filter: and(sourceFilter, workflowFilter),
    projectionId: {
      name: 'rawlogs.limited',
      builtIn: true,
    },
    distinct: true,
    sorting: [],
  };

  const fieldKeysQuery: DwQueryRequest = {
    select: {
      dims: [
        {
          name: 'COLLECT_DISTINCT(EXPLODE(rawlogs.DOCUMENT_KEYS))',
          alias: 'fieldKeys',
        },
      ],
      metrics: [],
    },
    filter: and(sourceFilter, workflowFilter),
    projectionId: {
      name: 'rawlogs',
      builtIn: true,
    },
    distinct: true,
    sorting: [],
  };

  const queryEnabled = enabled && !!workflowId;

  const { data: fieldTypesData, isLoading: isLoadingFieldTypes } = useQueryObject({
    queryObject: fieldTypesQuery,
    options: { enabled: queryEnabled, placeholderData: keepPreviousData },
    onSuccess: data => data?.[0].fieldTypes,
  });

  const { data: fieldKeysData, isLoading: isLoadingFieldKeys } = useQueryObject({
    queryObject: fieldKeysQuery,
    options: { enabled: queryEnabled, placeholderData: keepPreviousData },
    onSuccess: data => data?.[0].fieldKeys,
  });
  const fieldTypes = useMemo(() => fieldTypesData ?? [], [fieldTypesData]);
  const keysFromTypes = useMemo(() => fieldTypes?.map(({ path }) => path) || [], [fieldTypesData]); // this is for old files that don't have fieldKeys
  const keys = useMemo(() => (fieldKeysData?.length ? fieldKeysData : keysFromTypes), [fieldKeysData]);
  const fields = useMemo(() => buildKeyTree(keys && keys.length > 0 ? keys : keysFromTypes, fieldTypes), [keys, fieldTypes]);
  return useMemo(
    () => ({
      keys,
      fields,
      fieldTypesMap: fieldTypes.reduce(
        (acc, { path, type }) => {
          acc[path] = typeToFormatterResolver[type] || typeToFormatterResolver.Text;
          return acc;
        },
        {} as Record<string, string>
      ),
      hasLoaded: !!fieldTypesData && !!fieldKeysData,
      isLoading: isLoadingFieldTypes || isLoadingFieldKeys,
    }),
    [keys, fields, fieldTypes, fieldTypesData, fieldKeysData, isLoadingFieldTypes, isLoadingFieldKeys]
  );
};
